import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import type { ShouldRevalidateFunction } from 'react-router';
import { z } from 'zod';
import { getUserActiveStatuses } from '~/modules/user/api/get-user-active-statuses.server';
import { isUserLoggedIn } from '~/modules/user/hook/use-user';

export const USER_ACTIVE_STATUS_API_ROUTE = '/remix-api/user/active-status';

/**
 * Maximum member IDs accepted per request. Matches the client batch size in
 * `useBatchLoadUserActiveStatuses`; presence statuses are per-request DB work
 * on the backend, so unbounded lists are rejected rather than truncated.
 */
const MAX_MEMBER_IDS_PER_REQUEST = 50;

const QUERY_SCHEMA = z.object({
    memberIDs: z
        .array(z.coerce.number().int().positive())
        .max(MAX_MEMBER_IDS_PER_REQUEST),
});

/**
 * API endpoint to get the active/online status for a list of users.
 * Use with the `useUserActiveStatus` hook.
 * @see {@link packages/web/app/modules/user/hook/use-user-active-status.ts}
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const { logger, user } = context;

    // Presence is only visible to logged-in users (statuses resolve against
    // their contacts). Skip the GraphQL round-trip entirely for guests.
    if (!isUserLoggedIn(user)) {
        return routerData(null, { status: 401 });
    }

    const url = new URL(request.url);
    const result = QUERY_SCHEMA.safeParse({
        memberIDs: url.searchParams.getAll('memberIDs'),
    });

    if (!result.success) {
        return routerData(null, { status: 400 });
    }

    try {
        const statuses = await getUserActiveStatuses({
            context,
            memberIDs: result.data.memberIDs,
        });

        if (!statuses) {
            return routerData(null, { status: 500 });
        }

        return { statuses };
    } catch (error) {
        logger.error(`Error in ${USER_ACTIVE_STATUS_API_ROUTE}`, { error });
        return routerData(null, { status: 500 });
    }
};

export const shouldRevalidate: ShouldRevalidateFunction = () => {
    return false;
};
